import { ReactNode, useMemo, type FC } from 'react';
import styles from './ActivityItemIcon.module.css';
import type { ActivityItemIconProps } from './ActivityItemIcon.types';
import { Flex, Icon } from '@/_components';
import { IconProps } from '@/_components/Icon/Icon.types';
import { getClasses } from '@/_utils/classnames';

export const ActivityItemIcon: FC<ActivityItemIconProps> = ({
  color,
  title,
  typeId,
}) => {
  const icon: ReactNode = useMemo(() => {
    const iconProps: Pick<IconProps, 'size' | 'internalIconWidth'> = {
      size: 16,
      internalIconWidth: 'fill',
    };

    if (title?.includes('was submitted') || title?.includes(' completed')) {
      return <Icon name="check" {...iconProps} />;
    }

    if (title?.includes('was locked')) {
      return <Icon name="lock" {...iconProps} />;
    }

    if (title?.includes('was unlocked')) {
      return <Icon name="lock-open" {...iconProps} />;
    }

    if (title?.includes('was exported')) {
      return <Icon name="download" {...iconProps} />;
    }

    if (title?.includes('was sent')) {
      return <Icon name="send" {...iconProps} />;
    }

    switch (typeId) {
      case 1: // detail_change
        return <Icon name="info" {...iconProps} />;
      case 2: // document_related
        return <Icon name="folder" {...iconProps} />;
      case 3: // questionnaire_related
        return <Icon name="file" {...iconProps} />;
      case 4: // conversation_related
        return <Icon name="chat" {...iconProps} />;
      case 5: // tax_return_delivery_related
        return <Icon name="scroll" {...iconProps} />;
      case 6: // review_and_export_related
        return <Icon name="link" {...iconProps} />;
      case 7: // signature_request_related
        return <Icon name="signature" {...iconProps} />;
      case 0:
      default:
        return <Icon name="radio-button" size={16} />;
    }
  }, [title, typeId]);

  const activityItemIndicatorClasses = getClasses(
    styles.activityItemIndicator,
    {
      [styles.activityItemIndicatorGray]: color === 'gray',
      [styles.activityItemIndicatorGreen]: color === 'green',
      [styles.activityItemIndicatorBlue]: color === 'blue',
      [styles.activityItemIndicatorOrange]: color === 'orange',
      [styles.activityItemIndicatorTeal]: color === 'teal',
    },
  );

  return (
    <Flex className={styles.activityItemIcon} isCentered={true}>
      {color && <div className={activityItemIndicatorClasses} />}
      {icon}
    </Flex>
  );
};
